import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, Clock, MapPin, User, ArrowRight, 
  CalendarClock, MessageCircle, RefreshCw, X, CalendarPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { customerAPI } from '../../services/api';
import { generateGoogleCalendarUrl, downloadICSFile } from '../../utils/calendarUtils';

const UpcomingAppointmentCard = ({ onMessageWorker }) => {
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcoming();
  }, []);

  const fetchUpcoming = async () => {
    try {
      const res = await customerAPI.getUpcomingAppointment();
      setAppointment(res.data?.upcoming || null);
    } catch (error) {
      console.error('Failed to fetch upcoming appointment:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-6 mb-8 animate-pulse">
        <div className="h-6 bg-[var(--background-subtle)] rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-[var(--background-subtle)] rounded w-2/3 mb-2"></div>
        <div className="h-4 bg-[var(--background-subtle)] rounded w-1/2"></div>
      </div>
    );
  }

  if (!appointment) {
    return null;
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-[var(--warning)]/15 text-[var(--warning)] border-yellow-300',
      confirmed: 'bg-[var(--success)]/15 text-[var(--success)] border-green-300',
      in_progress: 'bg-[#D4AF37]/10 text-blue-800 border-blue-300'
    };
    return colors[status] || 'bg-[var(--background-subtle)] text-[var(--text-primary)]';
  };

  const getDaysUntilText = (days) => {
    if (days === 0) return { text: 'Today!', color: 'text-[var(--error)]', bg: 'bg-[var(--error)]/15' };
    if (days === 1) return { text: 'Tomorrow', color: 'text-orange-600', bg: 'bg-orange-100' };
    if (days <= 7) return { text: `In ${days} days`, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' };
    return { text: `In ${days} days`, color: 'text-[var(--text-secondary)]', bg: 'bg-[var(--background-subtle)]' };
  };

  const daysInfo = getDaysUntilText(appointment.days_until);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-0 mb-8 overflow-hidden border-2 border-[var(--primary)] bg-gradient-to-r from-[var(--primary-light)] to-white"
      data-testid="upcoming-appointment-card"
    >
      {/* Header */}
      <div className="bg-[var(--primary)] text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5" />
          <span className="font-bold">Upcoming Appointment</span>
        </div>
        <span className={`text-sm font-bold px-3 py-1 rounded-full ${daysInfo.bg} ${daysInfo.color}`}>
          {daysInfo.text}
        </span>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h3 className="text-xl font-bold">{appointment.service_name}</h3>
              <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(appointment.status)}`}>
                {appointment.status.replace('_', ' ')}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <Calendar className="w-4 h-4 text-[var(--primary)]" />
                <span className="font-medium">{appointment.scheduled_date}</span>
              </div>
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <Clock className="w-4 h-4 text-[var(--primary)]" />
                <span>{appointment.scheduled_time} ({appointment.duration_hours}h)</span>
              </div>
              <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                <MapPin className="w-4 h-4 text-[var(--primary)]" />
                <span className="truncate">{appointment.city}, {appointment.state}</span>
              </div>
            </div>

            {appointment.worker_name && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border-primary)]">
                <User className="w-4 h-4 text-[var(--secondary)]" />
                <span className="text-sm">
                  Worker: <span className="font-medium">{appointment.worker_name}</span>
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 md:min-w-[160px]">
            <div className="text-right mb-2">
              <p className="text-2xl font-bold text-[var(--primary)]">${Number(appointment.total_amount || 0).toFixed(2)}</p>
              <p className={`text-xs ${appointment.payment_status === 'paid' ? 'text-[var(--success)]' : 'text-[var(--warning)]'}`}>
                {appointment.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
              </p>
            </div>
            
            {/* Add to Calendar Button */}
            <div className="relative group">
              <Button 
                size="sm" 
                variant="outline"
                className="w-full border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white"
                data-testid="add-to-calendar-btn"
                onClick={() => window.open(generateGoogleCalendarUrl(appointment), '_blank')}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Add to Calendar
              </Button>
              <div className="absolute top-full right-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none group-hover:pointer-events-auto">
                <div className="bg-[var(--background-paper)] border border-[var(--border-primary)] rounded-lg shadow-lg p-1 min-w-[140px]">
                  <button 
                    onClick={(e) => { e.stopPropagation(); window.open(generateGoogleCalendarUrl(appointment), '_blank'); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--background-subtle)] rounded flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    Google Calendar
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); downloadICSFile(appointment); }}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--background-subtle)] rounded flex items-center gap-2"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    Download .ics
                  </button>
                </div>
              </div>
            </div>
            
            {appointment.worker_id && onMessageWorker && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onMessageWorker(appointment.id)}
                className="w-full"
                data-testid="message-worker-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Message Worker
              </Button>
            )}
            
            {appointment.payment_status !== 'paid' && appointment.status !== 'cancelled' && (
              <Link to={`/customer/pay/${appointment.id}`}>
                <Button size="sm" className="btn-primary w-full" data-testid="pay-now-btn">
                  Pay Now <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UpcomingAppointmentCard;
